import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';

import { AddIcon, Button, DeleteIcon, Text, dialogService, toastService } from '@thefarmersfront/kitchen';

import { Table } from '~/components/common';

import {
  useGetOwnerSupplierManagerOwnerSupplierCodeList,
  usePatchOwnerSupplierManagerDelete,
  usePatchOwnerSupplierManagerModify,
  usePostOwnerSupplierManager,
} from '~/hooks/vender';
import { useLoadingStore } from '~/store';
import { getVenderSupplierDetailManagerInfoColumnDefs } from '~/utils';

import { TOAST_MESSAGE } from '~/constants';

import { VenderSupplierManagerAddModal, VenderSupplierManagerModifyModal } from '..';

import type { TypeErrorResponseData } from '~/types/common.types';
import type {
  TypeOwnerSupplierManager,
  TypeOwnerSupplierManagerWithIdAndCode,
  TypeSupplierDetailFormValue,
} from '~/types/vender.types';

import styles from './VenderSupplierDetailModalManagerInfoSection.module.scss';

type TypeVenderSupplierDetailModalManagerInfoSectionProps = {
  ownerCode: string;
  ownerSupplierCode: string;
  venderSupplierDetailForm: TypeSupplierDetailFormValue;
  setVenderSupplierDetailForm: Dispatch<SetStateAction<TypeSupplierDetailFormValue>>;
};

/**
 * [화주사정보관리 > 공급사관리] 공급사 상세 모달 컴포넌트 > 담당자 정보 영역
 */
const VenderSupplierDetailModalManagerInfoSection = ({
  ownerCode,
  ownerSupplierCode,
  venderSupplierDetailForm,
  setVenderSupplierDetailForm,
}: TypeVenderSupplierDetailModalManagerInfoSectionProps) => {
  const { setIsLoading } = useLoadingStore();

  const [selectedManagerInfo, setSelectedManagerInfo] = useState<TypeOwnerSupplierManagerWithIdAndCode | null>(null);
  const [selectedManagerRowsList, setSelectedManagerRowsList] = useState<TypeOwnerSupplierManagerWithIdAndCode[]>([]);

  const [isSupplierManagerAddModalOpen, setIsSupplierManagerAddModalOpen] = useState(false);
  const [isSupplierManagerModifyModalOpen, setIsSupplierManagerModifyModalOpen] = useState(false);

  const { getOwnerSupplierManagerOwnerSupplierCodeList } = useGetOwnerSupplierManagerOwnerSupplierCodeList();
  const { postOwnerSupplierManager } = usePostOwnerSupplierManager();
  const { patchOwnerSupplierManagerDelete } = usePatchOwnerSupplierManagerDelete();
  const { patchOwnerSupplierManagerModify } = usePatchOwnerSupplierManagerModify();

  const handleSupplierManagerModifyClick = useCallback((data: TypeOwnerSupplierManagerWithIdAndCode) => {
    setIsSupplierManagerModifyModalOpen(true);
    setSelectedManagerInfo(data);
  }, []);

  const { columnDefs } = useMemo(
    () => getVenderSupplierDetailManagerInfoColumnDefs({ onClickViewDetail: handleSupplierManagerModifyClick }),
    [handleSupplierManagerModifyClick]
  );

  // 첫 행은 대표 담당자라 선택·삭제할 수 없다.
  const representativeManager = venderSupplierDetailForm.managerInfoList[0];

  const fetchSelectedManagersDelete = async () => {
    const selectedSupplierManagerIdList = selectedManagerRowsList.map(
      ({ ownerSupplierManagerId }) => ownerSupplierManagerId
    );

    try {
      setIsLoading(true);

      const params = {
        ownerCode: ownerCode,
        ownerSupplierManagerIds: selectedSupplierManagerIdList,
      };
      await patchOwnerSupplierManagerDelete(params);

      const updatedManagerList = await getOwnerSupplierManagerOwnerSupplierCodeList({
        ownerSupplierCode,
      });

      setVenderSupplierDetailForm({
        ...venderSupplierDetailForm,
        managerInfoList: updatedManagerList,
      });

      toastService.addToast({
        message: '성공적으로 저장되었습니다.',
      });
    } catch (error) {
      toastService.addToast({
        type: 'error',
        message: (error as TypeErrorResponseData['error'])?.message || TOAST_MESSAGE.DEFAULT_ERROR,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOwnerSupplierManagerModify = async (managerInfo: TypeOwnerSupplierManagerWithIdAndCode) => {
    try {
      setIsLoading(true);

      await patchOwnerSupplierManagerModify(managerInfo);

      const updatedManagerList = await getOwnerSupplierManagerOwnerSupplierCodeList({
        ownerSupplierCode,
      });

      setVenderSupplierDetailForm({
        ...venderSupplierDetailForm,
        managerInfoList: updatedManagerList,
      });
      setIsSupplierManagerModifyModalOpen(false);

      toastService.addToast({
        message: '성공적으로 저장되었습니다.',
      });
    } catch (error) {
      toastService.addToast({
        type: 'error',
        message: (error as TypeErrorResponseData['error'])?.message || TOAST_MESSAGE.DEFAULT_ERROR,
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * ========== 담당자 추가 모달 ==========
   */
  const handleSupplierManagerAddClick = () => setIsSupplierManagerAddModalOpen(true);

  const handleSupplierManagerAddModalCancel = () => setIsSupplierManagerAddModalOpen(false);

  const handleSupplierManagerAddModalConfirm = async (managerInfo: TypeOwnerSupplierManager) => {
    try {
      setIsLoading(true);

      const params = { ...managerInfo, ownerSupplierCode };
      await postOwnerSupplierManager(params);

      const updatedManagerList = await getOwnerSupplierManagerOwnerSupplierCodeList({
        ownerSupplierCode,
      });

      setVenderSupplierDetailForm({
        ...venderSupplierDetailForm,
        managerInfoList: updatedManagerList,
      });
      setIsSupplierManagerAddModalOpen(false);

      toastService.addToast({
        message: '성공적으로 저장되었습니다.',
      });
    } catch (error) {
      toastService.addToast({
        type: 'error',
        message: (error as TypeErrorResponseData['error'])?.message || TOAST_MESSAGE.DEFAULT_ERROR,
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * ========== 담당자 삭제 다이알로그 ==========
   */
  const handleSupplierManagerDeleteClick = () => {
    dialogService.addDialog({
      type: 'confirm',
      children: (
        <>
          <p>해당 정보는 즉시 삭제 처리됩니다.</p>
          <p>담당자 정보를 정말 삭제하시겠습니까?</p>
        </>
      ),
      status: 'error',
      confirmText: '삭제',
      onConfirm: fetchSelectedManagersDelete,
    });
  };

  /**
   * ========== 담당자 수정 모달 ==========
   */
  const handleSupplierManagerModifyModalCancel = () => setIsSupplierManagerModifyModalOpen(false);

  const handleSupplierManagerModifyModalConfirm = (updatedManagerInfo: unknown) => {
    fetchOwnerSupplierManagerModify(updatedManagerInfo as TypeOwnerSupplierManagerWithIdAndCode);
  };

  return (
    <>
      <div className={styles['manager-info-section']}>
        <div className={styles['table-title-wrapper']}>
          <div className={styles['table-title-side-field']}>
            <Text size="xxlarge" weight="bold" element="span">
              담당자 정보
            </Text>
            <Text weight="semi-bold" color="tertiary" element="span">
              {`${venderSupplierDetailForm.managerInfoList.length}/5`}
            </Text>
          </div>
          {
            <div className={styles['table-title-side-field']}>
              <Button
                size="small"
                color="tertiary"
                variant="outlined"
                leftIcon={<DeleteIcon size={16} />}
                disabled={selectedManagerRowsList.length === 0}
                onClick={handleSupplierManagerDeleteClick}
              >
                삭제
              </Button>
              <Button
                size="small"
                color="secondary"
                leftIcon={<AddIcon color="white" size={16} />}
                disabled={venderSupplierDetailForm.managerInfoList.length >= 5}
                onClick={handleSupplierManagerAddClick}
              >
                추가
              </Button>
            </div>
          }
        </div>
        <div className={styles['table-wrapper']}>
          <Table
            data={venderSupplierDetailForm.managerInfoList}
            columns={columnDefs}
            rowStatusType="SEARCH_EMPTY"
            selection={{
              onChange: setSelectedManagerRowsList,
              isRowSelectable: (manager) => manager !== representativeManager,
              showDisabledCheckboxes: true,
              dimUnselectableRows: true,
            }}
          />
        </div>
      </div>
      {isSupplierManagerAddModalOpen && (
        <VenderSupplierManagerAddModal
          open={isSupplierManagerAddModalOpen}
          onCancel={handleSupplierManagerAddModalCancel}
          onConfirm={handleSupplierManagerAddModalConfirm}
        />
      )}
      {isSupplierManagerModifyModalOpen && (
        <VenderSupplierManagerModifyModal
          open={isSupplierManagerModifyModalOpen}
          managerInfo={selectedManagerInfo}
          onCancel={handleSupplierManagerModifyModalCancel}
          onConfirm={handleSupplierManagerModifyModalConfirm}
        />
      )}
    </>
  );
};

export default VenderSupplierDetailModalManagerInfoSection;
